import { computed, inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EntitiesTableByLayerService } from '@connect-services/client/services/entities-table-by-layer.service';
import { ImmersiveViewService } from '@connect-services/client/services/immersive-view/immersive-view.service';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { SelectionStateService } from '@connect-services/client/services/map/selection/selection-state.service';
import { MapCreationGeomService } from '@connect-services/client/services/map-creation-geom.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';

interface MapTooltipState {
    currentEntityGeometryLength: number;
    hasActiveGeometryType: boolean;
    hasRecomputeError: boolean;
    hasSelectedEntities: boolean;
    immersiveView: boolean;
    isAddLineVertexActive: boolean;
    isCreateFieldIntervention: boolean;
    isPlanningSelectionActive: boolean;
    isSelectionActive: boolean;
    selectedEntitiesCount: number;
    selectedLayerCount: number;
}

@Injectable({
    providedIn: 'root',
})
export class MapTooltipService {
    public readonly isMapTooltipActive = computed<boolean>(() => !!this.mapTooltip());
    public readonly mapTooltip = computed<string>(() => {
        const state = this.buildState();

        if (this.isSelectionResolved(state)) {
            return '';
        }

        return this.computeMapTooltip(state);
    });

    private readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly editEntityDetailService = inject(EditEntityDetailService);
    private readonly entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    private readonly immersiveViewService = inject(ImmersiveViewService);
    private readonly mapCreationGeomService = inject(MapCreationGeomService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly selectionService = inject(SelectionService);
    private readonly selectionStateService = inject(SelectionStateService);
    private readonly translocoService = inject(TranslocoService);

    private buildState(): MapTooltipState {
        return {
            currentEntityGeometryLength: this.mapCreationGeomService.currentEntityGeometryLength(),
            hasActiveGeometryType: !!this.mapEditModeService.activeGeometryType(),
            hasRecomputeError: !!this.editEntityDetailService.recomputeError(),
            hasSelectedEntities: this.selectionStateService.hasSelectedEntities(),
            immersiveView:
                this.immersiveViewService.isImmersiveViewActive() && !this.immersiveViewService.markerPosition(),
            isAddLineVertexActive: this.mapEditModeService.isAddLineVertexActive(),
            isCreateFieldIntervention: this.createFieldInterventionService.isCreateFieldIntervention(),
            isPlanningSelectionActive: this.entitiesTableByLayerService.isPlanningSelectionActive(),
            isSelectionActive: this.selectionService.isSelectionActive(),
            selectedEntitiesCount: this.selectionStateService.selectedEntitiesCount(),
            selectedLayerCount: this.selectionStateService.selectedLayerCount(),
        };
    }

    private computeMapTooltip(state: MapTooltipState): string {
        const isAnySelectionModeActive =
            state.isSelectionActive || state.isCreateFieldIntervention || state.isPlanningSelectionActive;

        if (isAnySelectionModeActive) {
            if (!state.hasSelectedEntities) {
                return this.translocoService.translate('selection.clickEntityToSelect');
            }

            return this.translocoService.translate('selection.selectionSummary', {
                ENTITY_COUNT: state.selectedEntitiesCount,
                LAYER_COUNT: state.selectedLayerCount,
            });
        }

        if (state.currentEntityGeometryLength || state.isAddLineVertexActive) {
            return this.translocoService.translate('completeShape');
        }

        if (state.hasActiveGeometryType) {
            return this.translocoService.translate(
                state.hasRecomputeError ? 'editMode.recomputeErrorCorrectError' : 'createEntityClickMap',
            );
        }

        if (state.immersiveView) {
            return this.translocoService.translate('immersiveView.clickToViewPanorama');
        }

        return '';
    }

    private isSelectionResolved(state: MapTooltipState): boolean {
        return !!(
            (state.isSelectionActive && state.hasSelectedEntities) ||
            (state.isCreateFieldIntervention && this.createFieldInterventionService.selectedLayerNumber()) ||
            (state.isPlanningSelectionActive && this.entitiesTableByLayerService.selectedLayerNumber())
        );
    }
}
